import { JSNode } from "csi/json/JSNode";
import { C8NodeType, C8PrimitiveValue } from "csi/typings";
import util from "csi/util";
import xml from "csi/xml";
import _ from "lodash";

type QueryNodeFunction = (element: Element) => boolean | void;

/**
 * Iterate through element and child elements; if function evaluates to true, then stop and return current element
 */
function _iterate(xnRoot: Element, f: QueryNodeFunction): Element {
	if (f(xnRoot)) {
		return xnRoot;
	}

	let xnResult: Element;

	Array.from(xnRoot.childNodes).some((xn) => {
		xnResult = _iterate(xn as Element, f);
		return !!xnResult;
	});

	return xnResult;
}

class _Query {
	/** document holding query elements */
	readonly doc: XMLDocument;

	/** document root element */
	element: Element;

	constructor(doc?: XMLDocument, element?: Element) {
		if (doc) {
			this.doc = doc;
			this.element = element;
		} else {
			this.doc = util.newXMLDocument();
			this.element = this.doc.createElement(this.rootTag);
			this.doc.appendChild(this.element);
		}
	}

	/**
	 * Override to return the root tag for the XML query
	 */
	get rootTag(): string {
		return "";
	}

	/**
	 * Return Handle carriage returns correctly
	 */
	toString(): string {
		return util.serializeXML(this.doc).replace(/&amp;#xA;/g, "&#xA;");
	}

	/**
	 * If supported, return query string that bypasses query accelerator and goes direct to main database
	 */
	getDirectString(): string {
		return this.toString();
	}

	/**
	 * Return child elements of root element
	 */
	getElements(): Element[] {
		return [...this.element.childNodes].map((node: ChildNode) => node as Element);
	}

	/**
	 * Copy elements of given query into this query
	 * @param beforeElement - if specified then insert elements before given element rather than append to end
	 */
	importQuery(query: _Query, beforeElement?: Element): void {
		if (query) {
			this.importElements(query.getElements(), beforeElement);
		}
	}

	/**
	 * Copy given elements into query
	 * @param {Element[] | JSNode[]} newElements 
	 * @param {Element} [beforeElement] - if specified then insert elements before given element rather than append to end
	 */
	importElements(newElements: Element[] | JSNode[], beforeElement?: Element): void {
		const { element: parentElement } = this;
		const { ownerDocument } = parentElement;

		newElements.forEach((e: Element | JSNode) => {
			// support JSNode as well as standard elements
			const jsNode = e as JSNode;
			const element = e as Element;
			const newElement = jsNode.asXML?.(ownerDocument) || element.cloneNode(true) as Element;

			// remove text nodes (in this case, just whitespace) from imported elements - text nodes are not allowed in query schema
			util.traverseNode(newElement, (domNode) => {
				if (util.isTextNode(domNode)) {
					return util.TRAVERSE_DELETE;
				}
			});

			if (beforeElement) {
				parentElement.insertBefore(newElement, beforeElement);
			} else {
				parentElement.appendChild(newElement);
			}
		});
	}

	/**
	 * Return true if query has no child elements
	 */
	isEmpty(): boolean {
		return !this.size();
	}

	/**
	 * Adjust query based on node type being queried - default implementation does nothing
	 */
	setType(type: C8NodeType): this {
		// FIX: Move to Query?
		return this;
	}

	/**
	 * Return number of child elements
	 */
	size(): number {
		return this.element.childNodes.length;
	}

	/**
	 * Return element in query for which  given function evaluates to true
	 */
	_iterateElement(f: QueryNodeFunction): Element | undefined {
		return _iterate(this.element, f);
	}

	/**
	 * Set property on node only if value exists for it
	 */
	_setProperty(node: Element, key: string, value: C8PrimitiveValue): void {
		if (value != null) {
			node.setAttribute(key, value.toString());
		}
	}

	/**
	 * Set value property on node, transforming value as necessary
	 */
	_setValueProperty(node: Element, op: string, key: string, value: C8PrimitiveValue): boolean {
		if (value != null) {
			if (_.isString(value)) {
				// add % if not already there when matching
				if (op == xml.OP_MATCHES) {
					value = xml.getServerMatchValue(value);
				}

				value = value.replace(/\n/g, "&#xA;");
			} else {
				value = value.toString();
			}

			node.setAttribute(key, value);

			return true;
		}

		return false;
	}
}

export = _Query;
